import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  useTheme,
} from "@mui/material";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import TableChartRoundedIcon from "@mui/icons-material/TableChartRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";

function docIcon(name, theme) {
  const n = (name || "").toLowerCase();
  if (n.endsWith(".pdf")) return <PictureAsPdfRoundedIcon sx={{ color: "error.main", fontSize: 28 }} />;
  if (n.endsWith(".xls") || n.endsWith(".xlsx")) return <TableChartRoundedIcon sx={{ color: "success.main", fontSize: 28 }} />;
  return <DescriptionRoundedIcon sx={{ color: "primary.main", fontSize: 28 }} />;
}

export default function AgentDocumentsSection({
  documents = [],
  fileInputRef,
  handleClickAdd,
  handleDrop,
  handleDragOver,
  moveDocument,
  removeDocument,
  readOnly = false,
  accept,
  embedded = false,
}) {
  const theme = useTheme();

  const content = (
    <>
      {embedded && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          Documentos (PDF, Word, planilhas)
        </Typography>
      )}
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12}>
            <Box
              onDrop={readOnly ? undefined : handleDrop}
              onDragOver={readOnly ? undefined : handleDragOver}
              sx={{
                border: `1px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
                background:
                  theme.palette.mode === "dark"
                    ? "rgba(255,255,255,0.03)"
                    : "rgba(0,0,0,0.02)",
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DragIndicatorRoundedIcon fontSize="small" color="action" />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Arraste e solte documentos aqui</Typography>
                <Typography variant="caption" color="text.secondary">
                  ou{" "}
                  <Button
                    size="small"
                    variant="text"
                    onClick={readOnly ? undefined : handleClickAdd}
                    startIcon={<AttachFileRoundedIcon />}
                    disabled={readOnly}
                  >
                    selecionar arquivos
                  </Button>
                </Typography>
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept={accept}
                multiple
                hidden
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  const ev = new CustomEvent("agent-documents:addFiles", { detail: files });
                  window.dispatchEvent(ev);
                  e.target.value = "";
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
              Dica: PDF escaneado (apenas imagem) não produz texto para o agente. Use PDFs com texto selecionável ou
              descreva o conteúdo no campo de instruções do agente.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={1.5}>
              {documents.map((doc, idx) => (
                <Grid item key={doc.id ?? idx}>
                  <Box
                    sx={{
                      width: 140,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                      overflow: "hidden",
                      p: 1.5,
                      bgcolor: "background.paper",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box sx={{ mt: 0.5 }}>{docIcon(doc.name, theme)}</Box>
                    <Typography
                      variant="caption"
                      sx={{
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "100%",
                      }}
                    >
                      {doc.name}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                      <Chip size="small" label={`#${idx + 1}`} />
                      <Box>
                        <IconButton
                          size="small"
                          onClick={() => moveDocument(idx, -1)}
                          disabled={readOnly || idx === 0}
                        >
                          <ArrowBackIosNewRoundedIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => moveDocument(idx, 1)}
                          disabled={readOnly || idx === documents.length - 1}
                        >
                          <ArrowForwardIosRoundedIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    </Box>
                    {!readOnly && (
                      <IconButton
                        size="small"
                        onClick={() => removeDocument(idx)}
                        sx={{ alignSelf: "flex-end", mt: -0.5 }}
                        aria-label="Remover"
                      >
                        <DeleteOutlineRoundedIcon fontSize="inherit" />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
    </>
  );

  if (embedded) {
    return <Box sx={{ mb: 0 }}>{content}</Box>;
  }
  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardHeader
        title="Documentos de referência"
        subheader="Word, planilhas e PDF. Arraste e solte ou selecione os arquivos."
        sx={{ pb: 0.5, "& .MuiCardHeader-title": { fontSize: 16, fontWeight: 600 } }}
      />
      <CardContent sx={{ pt: 1 }}>{content}</CardContent>
    </Card>
  );
}
